import { BehaviorSubject, Subscription } from 'rxjs';
import {
  Component,
  ElementRef,
  ViewChild,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  makePresentationProposalScene,
  PresentationProposalScene,
} from 'src/app/phaser/scenes/presentation-proposal.scene';
import { GameInstanceService } from 'src/app/services/game-instance.service';
import { GameQuery } from 'src/app/state/gameState/game.query';
import { GameService } from 'src/app/state/gameState/game.service';
import { UserQuery } from 'src/app/state/userState/user.query';
import { createTimeline } from 'animejs';
import { EmptyScene, makeEmptyScene } from 'src/app/phaser/scenes/empty.scene';
import { IFinalVote } from 'src/app/models/api.model';
import { GameStatus } from '../../models/game.model';
import { WebSocketService } from 'src/app/services/websocket.service';
import moment from 'moment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TrackingService } from 'src/app/services/tracking.service';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'app-presentation-proposal-page',
  templateUrl: './presentation-proposal.page.html',
  styleUrls: ['./presentation-proposal.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PresentationProposalPage {
  @ViewChild('phaserWrapper') phaserWrapper!: ElementRef;
  topicTitle = '';
  pastFiveSeconds = signal(false);
  votingMode = signal(false);
  isProposalTourEnd = signal(false);
  nextVotingPhase = signal(false);
  startVoting = signal(false);
  showSVGResult = signal(false);
  votingTimerRound: number;
  height!: string;
  finalVotes: IFinalVote[] = [];
  votingStatusSubscription: Subscription;
  proposalTourSubscription: Subscription;
  votingFinished = signal(false);
  showGameResult = signal(false);
  approveModal = signal(false);
  rejectedModal = signal(false);
  rejectedFinalModal = signal(false);
  finalVotingModal = signal(false);
  voted = signal(false);
  votingRound!: number;
  gameStatus!: number;
  hideBox = signal(false);
  waitingToVote = signal(false);
  userVote = signal(false);
  loading = signal(false);
  loadingSubscription!: Subscription;
  isSingleMode = signal(false);
  votingEnded = signal(false);
  votingStarted?: string;
  isHost = signal(false);
  ticker!: ReturnType<typeof setTimeout>;
  isInformational = signal(false);
  europeanCommissionIntroduction = '';
  public sidebarTrigger = new BehaviorSubject<boolean>(false);

  constructor(
    private gameInstanceService: GameInstanceService,
    private router: Router,
    private gameService: GameService,
    private gameQuery: GameQuery,
    private trackingService: TrackingService,
    private userQuery: UserQuery,
    private webSocketService: WebSocketService,
    private translateService: TranslateService
  ) {
    this.votingTimerRound = this.gameQuery.getVotingTime();
    this.votingStatusSubscription = this.gameQuery
      .select('isVoting')
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        console.log('isVoting: ', res);
        this.votingMode.set(res);
        if (res) {
          this.votingFinished.set(false);
        }
      });
    this.proposalTourSubscription = this.gameQuery
      .select('isProposalTourEnd')
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        console.log('Proposal tour: ', res);
        this.isProposalTourEnd.set(res);
      });

    this.gameQuery
      .select('game')
      .pipe(untilDestroyed(this))
      .subscribe((game) => {
        console.log('Game: ', game);
        if (!game) {
          return;
        }
        this.isSingleMode.set(Boolean(game?.single));
        this.votingRound = game?.votingRound ?? 0;

        const user = this.userQuery.getValue().user;
        const currrentUserGame = game.userGames?.find(
          (ug) => ug.userId === user.userId
        );
        this.isHost.set(currrentUserGame?.userHost === 1);

        if (
          this.votingStarted !== game.votingStarted &&
          game.votingStarted &&
          game.votingStarted !== '0001-01-01T00:00:00Z'
        ) {
          this.waitingToVote.set(true);
          const votingTimeLeft = moment(new Date()).diff(
            game.votingStarted,
            'seconds'
          );
          if (votingTimeLeft > this.votingTimerRound) {
            this.onVotingEnd();
          }
        }
        this.votingStarted = game?.votingStarted;
        if (
          this.gameStatus !== game?.status &&
          game?.status === GameStatus.finalVote
        ) {
          this.pastFiveSeconds.set(false);
          this.nextVotingPhase.set(false);
          this.startVoting.set(false);
          // this.showSVGResult.set(false);
          this.finalVotes = [];
          this.showGameResult.set(false);
          this.approveModal.set(false);
          this.rejectedModal.set(false);
          this.rejectedFinalModal.set(false);
          this.finalVotingModal.set(false);
          this.voted.set(false);
          this.hideBox.set(false);
          this.waitingToVote.set(false);
          this.userVote.set(false);
          this.loading.set(false);
          this.votingEnded.set(false);
        } else if (
          this.gameStatus !== game?.status &&
          game?.status === GameStatus.approved
        ) {
          if (!this.votingEnded()) {
            return;
          }
          this.trackingService.click({
            name: 'VotingEnded',
            type: 'event',
          });
          console.log('GameStatus.approved');
          this.showGameResult.set(true);
          this.startVoting.set(false);
          this.rejectedModal.set(false);
          this.rejectedFinalModal.set(false);
          this.hideBox.set(true);
          setTimeout(() => {
            this.approveModal.set(true);
            setTimeout(() => {
              this.approveModal.set(false);
            }, 15000);
          }, 5000);
          // this.showSVGResult = false;
        } else if (
          this.gameStatus !== game?.status &&
          game?.status === GameStatus.rejected
        ) {
          if (!this.votingEnded()) {
            return;
          }
          this.trackingService.click({
            name: 'VotingEnded',
            type: 'event',
          });
          console.log('GameStatus.rejected');
          if (this.votingRound === 2) {
            console.log('GameStatus.rejected 2');
            setTimeout(() => {
              this.rejectedFinalModal.set(true);
              setTimeout(() => {
                this.rejectedFinalModal.set(false);
                this.showGameResult.set(true);
                // this.showSVGResult.set(true);
                this.voted.set(true);
              }, 15000);
            }, 5000);
          }
        } else if (
          this.votingMode() &&
          this.gameStatus !== game?.status &&
          (game?.status === GameStatus.started ||
            game?.status === GameStatus.break)
        ) {
          console.log('GameStatus.started');
          this.rejectedModal.set(true);
          this.ticker = setTimeout(() => {
            this.rejectedModal.set(false);
            this.onPhases();
          }, 15000);
        } else {
          this.showGameResult.set(
            game?.status === GameStatus.rejected ||
            game?.status === GameStatus.approved
          );
        }

        this.gameStatus = game?.status;

        if (game?.finalVotes?.length !== this.finalVotes?.length) {
          console.log('Game this.finalVotes: ', this.finalVotes);
          this.finalVotes = game?.finalVotes || [];
          const userId = this.userQuery.getUserId();
          this.voted.set(
            this.finalVotes.find((vote) => vote.userId === userId)
              ?.finalVote !== undefined
          );
          // console.log('Voted: ', this.voted);
          console.log('this.gameStatus: ', this.gameStatus);
          console.log('this.votingRound: ', this.votingRound);

          if (this.gameStatus === GameStatus.finalVote) {
            console.log('GameStatus.finalVote');
            if (!this.userVote()) {
              if (this.voted()) {
                this.startVoting.set(false); // TODO: should change
                // this.showSVGResult.set(true);
              } else {
                console.log('Not Voted');
                this.startVoting.set(true);
                // this.showSVGResult.set(false);
              }
            }
          } else {
            console.log('GameStatus nothing');
          }
        }
      });

    this.translateService.onLangChange
      .pipe(
        untilDestroyed(this),
        catchError((err) => {
          console.log('COULDNT TRANSLATE ', err);
          throw err;
        })
      )
      .subscribe(() => {
        this.sidebarTrigger.next(true);
      });
  }

  get onVotingEndFunc() {
    return this.onVotingEnd.bind(this);
  }

  ionViewWillEnter(): void {
    this.isInformational.set(this.gameQuery.getCurrentTopicIsInformational() ?? false);
    this.pastFiveSeconds.set(false);
    if (this.isInformational()) {
      this.europeanCommissionIntroduction =
        this.gameQuery.getCurrentTopic()?.europeanCommissionIntroduction ?? '';
    }
    this.userVote.set(false);
    this.topicTitle = this.gameQuery.getCurrentTopic()?.title ?? '';
    if (this.votingMode()) {
      document.getElementById('eu-council-game-container')!.style.width =
        'calc(100% - 15rem)';
      document.getElementById('eu-council-game-container')!.style.left = '17rem';
    }
    // Check before switchScene: if the scene is already in memory, preload/create won't
    // run again and loading will never transition true→false, so animations must fire directly.
    const sceneAlreadyLoaded = !!(this.gameInstanceService.gameInstance as any)
      ?.scene.getScene(PresentationProposalScene.key);

    this.gameInstanceService.switchScene(
      PresentationProposalScene.key,
      makePresentationProposalScene
    );
    this.gameInstanceService.isAnimatedSpeechPlaying$.next(true);

    if (sceneAlreadyLoaded) {
      // Scene was already loaded (revisiting the page) — trigger animations directly.
      this.runSpeechAnimations();
    } else {
      // Scene is loading for the first time — wait for the loading flag to clear.
      this.loadingSubscription = this.gameInstanceService.loading
        .pipe(untilDestroyed(this))
        .subscribe((res) => {
          if (!res && this.loading()) {
            this.runSpeechAnimations();
            this.loadingSubscription?.unsubscribe();
          }
          this.loading.set(res);
        });
    }
  }

  private runSpeechAnimations(): void {
    setTimeout(() => {
      if (!this.isProposalTourEnd()) {
        createTimeline({ loop: false }).add('.anim-4a .word', {
          scale: [4, 1],
          opacity: [0, 1],
          translateZ: 0,
          ease: 'outExpo',
          delay: (el, i) => 120 * i,
          onComplete: () => {
            createTimeline({ loop: false }).add('.anim-4b .word', {
              scale: [4, 1],
              opacity: [0, 1],
              translateZ: 0,
              ease: 'outExpo',
              duration: 950,
              delay: (el, i) => 120 * i,
            });
            this.gameInstanceService.isAnimatedSpeechPlaying$.next(false);
          },
        });
      } else {
        createTimeline({ loop: false }).add('.anim-6 .word', {
          scale: [4, 1],
          opacity: [0, 1],
          translateZ: 0,
          ease: 'outExpo',
          delay: (el, i) => 120 * i,
          onComplete: () => {
            this.gameInstanceService.isAnimatedSpeechPlaying$.next(false);
          },
        });
      }
    }, 200);

    setTimeout(() => {
      this.pastFiveSeconds.set(true);
      setTimeout(() => {
        this.gameInstanceService.isAnimatedSpeechPlaying$.next(true);
      createTimeline({ loop: false }).add('.anim-5a .word', {
        scale: [4, 1],
        opacity: [0, 1],
        translateZ: 0,
        ease: 'outExpo',
        delay: (el, i) => 120 * i,
        onComplete: () => {
          this.gameInstanceService.isAnimatedSpeechPlaying$.next(false);
          createTimeline({ loop: false }).add('.anim-5b .word', {
            scale: [4, 1],
            opacity: [0, 1],
            translateZ: 0,
            ease: 'outExpo',
            duration: 950,
            delay: (el, i) => 120 * i,
          });
        },
      });
      }, 200);
    }, 5000);
  }

  ionViewDidEnter(): void {
    setTimeout(() => {
      this.nextVotingPhase.set(true);
    }, 10000);

    if (this.votingMode()) {
      this.sidebarTrigger.next(true);
      setTimeout(() => {
        if (
          this.isHost() &&
          (!this.votingStarted || this.votingStarted === '0001-01-01T00:00:00Z')
        ) {
          this.gameService
            .startVote()
            .then((res) => {
              this.waitingToVote.set(true);
            })
            .catch((err) => console.log(err));
        }
      }, 10000);
    }
    this.gameInstanceService.gameInstance.scale.on('resize', this.resize, this);
    this.gameInstanceService.gameInstance.scale.refresh();
  }

  onVotingEnd(): void {
    console.log('onVotingEnd');
    this.votingEnded.set(true);
    this.startVoting.set(false);
    this.webSocketService.triggerGameUpdate();
  }

  ionViewWillLeave(): void {
    this.sidebarTrigger.next(false);
    document.getElementById('eu-council-game-container')!.style.width = '100%';
    document.getElementById('eu-council-game-container')!.style.left = '0';
    clearTimeout(this.ticker);
    this.gameInstanceService.switchScene(EmptyScene.key, makeEmptyScene);
  }

  onProposal() {
    this.router.navigate(['/proposal']);
  }

  onPhases() {
    clearTimeout(this.ticker);
    this.gameService.updateIsVoting(false);
    const phase = this.gameQuery.getTypeOfPhase();
    if (phase === 'negotiation') {
      this.router.navigate(['/negotiation_phase']);
    } else {
      this.router.navigate(['/coffee-break']);
    }
  }

  onNegotiationPhase() {
    this.router.navigate(['/negotiation_phase']);
  }

  resize() {
    if (this.phaserWrapper) {
      this.phaserWrapper.nativeElement.style.width =
        this.gameInstanceService.gameInstance.canvas.style.width;
      this.phaserWrapper.nativeElement.style.height =
        this.gameInstanceService.gameInstance.canvas.style.height;
      this.phaserWrapper.nativeElement.style.marginLeft =
        this.gameInstanceService.gameInstance.canvas.style.marginLeft;
      this.phaserWrapper.nativeElement.style.marginTop =
        this.gameInstanceService.gameInstance.canvas.style.marginTop;
    }
  }

  onUserVote(vote: boolean): void {
    this.startVoting.set(false); // shouldn't be set here...
    this.userVote.set(true);
    this.gameService.postFinalVote(vote).then((res) => {
      this.voted.set(true);
    });
  }

  onVotingApprovedModalClick(): void {
    this.approveModal.set(false);
  }

  onVotingRejectedFinalModalClick(): void {
    this.rejectedFinalModal.set(false);
  }

  onEndGame(): void {
    console.log('end game');
    this.finalVotingModal.set(true);
  }

  onGameResult(): void {
    this.trackingService.click({
      name: 'GameResults',
      type: 'event',
    });
    this.loading.set(true);
    this.gameService
      .gameData()
      .then(() =>
        this.isSingleMode()
          ? this.router.navigate(['/game_results'])
          : this.router.navigate(['/multiplayer_game_results'])
      )
      .catch((err) => console.log(err))
      .finally(() => {
        this.loading.set(false);
      });
  }

  onNegotiationPhasePage(): void {
    this.gameService.updateProposalTour(false);
    this.router.navigate(['/negotiation_phase']);
  }
}
